/* Thoughtful Fit PDP gallery — only the product detail page.
 * Color groups are managed from Shopify Admin > Products > Media > Alt text.
 * Example: "Front of navy blue tee #color=Navy Blue".
 * Until tagged, original media remain visible. This is intentionally scoped to
 * the existing independent mobile gallery; desktop uses native Horizon gallery.
 */
(() => {
  const mounted = new WeakSet();
  const slug = (value) => String(value || '').trim().toLowerCase()
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  function setup(root) {
    root.querySelectorAll('[data-tf-mobile-gallery]').forEach((gallery) => {
      if (mounted.has(gallery)) return;
      mounted.add(gallery);

      const slides = [...gallery.querySelectorAll('[data-tf-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-tf-thumbnail]')];
      const count = gallery.querySelector('[data-tf-count]');
      const dialog = gallery.querySelector('[data-tf-zoom-dialog]');
      const zoomContent = gallery.querySelector('[data-tf-zoom-content]');
      const prev = gallery.querySelector('[data-tf-prev]');
      const next = gallery.querySelector('[data-tf-next]');
      const rootComponent = gallery.closest('.tf-pdp');
      let visible = slides.map((_, i) => i);
      let active = 0;
      let selectedColor = gallery.dataset.tfInitialColor || '';
      let focusBack = null;
      let touchStart = null;
      let variantData = [];
      try { variantData = JSON.parse(gallery.querySelector('[data-tf-variant-data]')?.textContent || '[]'); } catch (_) { /* no data */ }

      function picker() { return rootComponent?.querySelector('variant-picker'); }
      function selections() {
        const current = picker();
        if (!current) return [];
        const fields = [...current.querySelectorAll('fieldset[data-fieldset-index]')];
        const values = fields.map((field) => field.querySelector('input:checked')?.value ?? '').filter(Boolean);
        // Dropdowns use a separate structure; if no radio fieldsets exist.
        if (!fields.length) return [...current.querySelectorAll('select[name^="options["]')].map((s) => s.value);
        return values;
      }
      function readColor() {
        const current = picker();
        if (!current) return selectedColor;
        const expectedName = slug(gallery.dataset.tfColorOption || 'Color');
        const matches = (v) => ['color', 'colour', expectedName].includes(slug(v));
        const input = [...current.querySelectorAll('input:checked[data-option-name]')]
          .find((node) => matches(node.dataset.optionName));
        if (input) return input.value;
        const select = [...current.querySelectorAll('select[name^="options["]')]
          .find((node) => matches(node.name.replace(/^options\[/, '').replace(/\]$/, '')));
        return select?.value || selectedColor;
      }
      function variantMediaId() {
        const options = selections();
        const variant = variantData.find((entry) => options.length && options.every((val, i) => entry.options?.[i] === val));
        return variant?.featured_media?.id || null;
      }
      function show(index) {
        if (!visible.length) return;
        active = visible.includes(index) ? index : visible[0];
        slides.forEach((slide, i) => {
          slide.hidden = i !== active;
          if (i !== active) slide.querySelectorAll('video').forEach((video) => video.pause());
        });
        thumbs.forEach((button, i) => {
          const picked = i === active;
          button.classList.toggle('is-current', picked);
          button.setAttribute('aria-current', String(picked));
        });
        if (count) count.textContent = `${visible.indexOf(active) + 1} / ${visible.length}`;
        thumbs[active]?.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      }
      function filterColor(color, tryFeatured = true) {
        selectedColor = color || selectedColor;
        const desired = slug(selectedColor);
        const groupingEnabled = gallery.dataset.tfFilterColors === 'true';
        const hasGroup = groupingEnabled && desired && slides.some((slide) => slide.dataset.colorGroup === desired);
        visible = slides.map((_, i) => i).filter((i) => !hasGroup ||
          slides[i].dataset.colorGroup === desired || slides[i].dataset.colorGroup === 'all');
        thumbs.forEach((button, i) => { button.hidden = !visible.includes(i); });
        if (prev) prev.hidden = visible.length <= 1;
        if (next) next.hidden = visible.length <= 1;
        if (count) count.hidden = visible.length <= 1;

        const mediaId = tryFeatured ? variantMediaId() : null;
        const featuredIndex = mediaId ? slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId)) : -1;
        show(featuredIndex >= 0 && visible.includes(featuredIndex) ? featuredIndex : visible[0]);
      }
      function step(amount) {
        const pos = visible.indexOf(active);
        show(visible[(pos + amount + visible.length) % visible.length]);
      }
      prev?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));
      thumbs.forEach((button, i) => button.addEventListener('click', () => {
        if (visible.includes(i)) show(i);
      }));
      const stage = gallery.querySelector('.tf-mobile-gallery__stage');
      stage?.addEventListener('touchstart', (event) => {
        if (event.touches.length === 1) touchStart = [event.touches[0].clientX, event.touches[0].clientY];
      }, { passive: true });
      stage?.addEventListener('touchend', (event) => {
        if (!touchStart || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - touchStart[0];
        const dy = event.changedTouches[0].clientY - touchStart[1];
        touchStart = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.35) step(dx < 0 ? 1 : -1);
      }, { passive: true });
      gallery.querySelector('[data-tf-zoom-open]')?.addEventListener('click', (event) => {
        const img = slides[active]?.querySelector('img');
        if (!img || !dialog) return;
        focusBack = event.currentTarget;
        const clone = img.cloneNode(true);
        clone.removeAttribute('srcset'); clone.removeAttribute('sizes');
        zoomContent.replaceChildren(clone);
        dialog.showModal();
        dialog.querySelector('[data-tf-zoom-close]')?.focus();
      });
      gallery.querySelector('[data-tf-zoom-close]')?.addEventListener('click', () => dialog?.close());
      dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog?.addEventListener('close', () => { zoomContent.replaceChildren(); focusBack?.focus(); });

      function syncColor() {
        const color = readColor();
        if (color !== selectedColor) filterColor(color);
        else {
          // Size changes can select another featured media within the same group.
          const mediaId = variantMediaId();
          const i = mediaId ? slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId)) : -1;
          if (i >= 0 && visible.includes(i) && i !== active) show(i);
        }
        // Keep merchant's option label and selected swatch in sync immediately,
        // and rely on Horizon's normal server-side variant morph thereafter.
        rootComponent?.querySelectorAll('[data-tf-color-name]').forEach((node) => {
          if (node.classList.contains('tf-pdp-selected-color')) node.textContent = `: ${color}`;
          else node.textContent = color;
        });
      }
      rootComponent?.addEventListener('change', (event) => {
        if (event.target.closest('variant-picker')) requestAnimationFrame(syncColor);
      });
      rootComponent?.addEventListener('shopify:product:select', (event) => {
        event.promise?.then(() => { if (gallery.isConnected) syncColor(); }).catch(() => {});
      });
      filterColor(readColor());
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(document), { once: true });
  else setup(document);
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
